<div class="h-full flex flex-col">
  <div class="flex-1 flex flex-col relative">

    <!-- Header -->
    <div class="p-6 pb-3">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <button type="button" (click)="onBack()" class="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5 mr-1">
              <path fill-rule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clip-rule="evenodd" />
            </svg>
            Back to ICPs
          </button>
        </div>

        <!-- Actions -->
        <div class="flex items-center space-x-3" *ngIf="icp$ | async as icp">
          <button type="button" (click)="onEdit()" class="inline-flex items-center gap-x-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white shadow-xs hover:opacity-90">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4">
              <path d="m2.695 14.762-1.262 3.155a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.886L17.5 5.501a2.121 2.121 0 0 0-3-3L3.58 13.419a4 4 0 0 0-.885 1.343Z" />
            </svg>
            Edit ICP
          </button>
        </div>
      </div>

      <!-- Error State -->
      <div *ngIf="error$ | async as error" class="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        <p class="font-semibold">Error:</p>
        <p>{{ error }}</p>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading$ | async" class="flex-1 flex items-center justify-center">
      <div class="text-center">
        <svg class="animate-spin h-8 w-8 text-primary mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="mt-2 text-sm text-gray-500">Loading ICP details...</p>
      </div>
    </div>

    <!-- Content -->
    <div *ngIf="!(loading$ | async) && (icp$ | async) as icp" class="flex-1 overflow-y-auto px-6 pb-6">
      <div>

        <!-- ICP Header Card -->
        <div class="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div class="flex items-start justify-between">
            <div class="flex items-center space-x-4">
              <div class="size-16 rounded-lg bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                <span class="text-violet-500 text-lg font-semibold">{{ icp.name?.charAt(0)?.toUpperCase() || 'I' }}</span>
              </div>
              <div>
                <h2 class="text-xl font-semibold text-gray-900">{{ icp.name || 'Unnamed ICP' }}</h2>
                <p class="text-sm text-gray-500 mt-1">{{ icp.description || 'No description provided' }}</p>
                <div class="flex items-center space-x-4 mt-2">
                  <span [class]="icp.is_default ? 'bg-violet-500/10 text-violet-500' : 'bg-gray-100 text-gray-800'" class="inline-flex rounded-full px-2 py-1 text-xs font-medium">
                    {{ icp.is_default ? 'Default ICP' : 'Custom ICP' }}
                  </span>
                  <span class="text-xs text-gray-500">Created {{ formatDate(icp.created_at) }}</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Workspaces Section -->
        <div class="bg-white rounded-lg border border-gray-200 px-6 py-4 mb-6">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 mr-2 text-violet-500">
                <path fill-rule="evenodd" d="M6 3.75A2.75 2.75 0 0 1 8.75 1h2.5A2.75 2.75 0 0 1 14 3.75v.443c.572.055 1.14.122 1.706.2C17.053 4.582 18 5.75 18 7.07v3.469c0 1.126-.694 2.191-1.83 2.54-1.952.599-4.024.921-6.17.921s-4.219-.322-6.17-.921C2.694 12.73 2 11.665 2 10.539V7.07c0-1.321.947-2.489 2.294-2.676A41.047 41.047 0 0 1 6 4.193V3.75Zm6.5 0v.325a41.622 41.622 0 0 0-5 0V3.75c0-.69.56-1.25 1.25-1.25h2.5c.69 0 1.25.56 1.25 1.25ZM10 10a1 1 0 0 0-1 1v.01a1 1 0 0 0 1 1h.01a1 1 0 0 0 1-1V11a1 1 0 0 0-1-1H10Z" clip-rule="evenodd" />
                <path d="M3 15.055v-.684c.126.053.255.1.39.142 2.092.642 4.313.987 6.61.987 2.297 0 4.518-.345 6.61-.987.135-.041.264-.089.39-.142v.684c0 1.347-.985 2.53-2.363 2.686a41.454 41.454 0 0 1-9.274 0C3.985 17.585 3 16.402 3 15.055Z" />
              </svg>
              <h4 class="text-sm font-semibold text-gray-900">Workspaces</h4>
            </div>
            <div *ngIf="icp.workspaces && icp.workspaces.length > 0" class="flex flex-wrap gap-2">
              <a *ngFor="let ws of icp.workspaces"
                 [routerLink]="['/workspaces', ws.id]"
                 class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white pl-1 pr-3 py-1 hover:border-violet-300 hover:bg-violet-50 transition-colors cursor-pointer">
                <span class="inline-flex items-center justify-center size-7 rounded-full bg-violet-500 text-[11px] font-medium text-white flex-shrink-0">
                  {{ ws.name_acronym }}
                </span>
                <span class="text-sm text-gray-700">{{ ws.name }}</span>
              </a>
            </div>
            <p *ngIf="!icp.workspaces || icp.workspaces.length === 0" class="text-sm text-gray-500">No workspaces assigned</p>
          </div>
        </div>

        <!-- Details Sections -->
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">

          <!-- Solutions & Products -->
          <div class="bg-white rounded-lg border border-gray-200 p-4 xl:col-span-1">
            <h3 class="text-base font-semibold text-gray-900 mb-3 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 mr-1.5 text-violet-500">
                <path d="M10 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.465 14.493a1.23 1.23 0 0 0 .41 1.412A9.957 9.957 0 0 0 10 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 0 0-13.074.003Z" />
              </svg>
              Solutions & Products
            </h3>
            <div class="space-y-2">
              <div>
                <label class="text-xs font-medium text-gray-600">Benefits of Products/Solutions I Sell</label>
                <p class="text-sm text-gray-900">{{ getDisplayValue(icp.what_solutions_do_i_sell) }}</p>
              </div>
              <div>
                <label class="text-xs font-medium text-gray-600">Business Pains I Solve</label>
                <p class="text-sm text-gray-900">{{ getDisplayValue(icp.challenges_solved) }}</p>
              </div>
            </div>
          </div>

          <!-- Target Customer Information -->
          <div class="bg-white rounded-lg border border-gray-200 p-4 xl:col-span-1">
            <h3 class="text-base font-semibold text-gray-900 mb-3 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 mr-1.5 text-violet-500">
                <path d="M15.98 1.804a1 1 0 0 0-1.96 0l-.24 1.192a1 1 0 0 1-.784.785l-1.192.238a1 1 0 0 0 0 1.962l1.192.238a1 1 0 0 1 .785.785l.238 1.192a1 1 0 0 0 1.962 0l.238-1.192a1 1 0 0 1 .785-.785l1.192-.238a1 1 0 0 0 0-1.962l-1.192-.238a1 1 0 0 1-.785-.785l-.238-1.192ZM6.949 5.684a1 1 0 0 0-1.898 0l-.683 2.051a1 1 0 0 1-.633.633l-2.051.683a1 1 0 0 0 0 1.898l2.051.684a1 1 0 0 1 .633.632l.683 2.051a1 1 0 0 0 1.898 0l.683-2.051a1 1 0 0 1 .633-.633l2.051-.683a1 1 0 0 0 0-1.898l-2.051-.683a1 1 0 0 1-.633-.633L6.95 5.684ZM13.949 13.684a1 1 0 0 0-1.898 0l-.184.551a1 1 0 0 1-.632.633l-.551.183a1 1 0 0 0 0 1.898l.551.183a1 1 0 0 1 .633.633l.183.551a1 1 0 0 0 1.898 0l.184-.551a1 1 0 0 1 .632-.633l.551-.183a1 1 0 0 0 0-1.898l-.551-.184a1 1 0 0 1-.633-.632l-.183-.551Z" />
              </svg>
              Target Customers
            </h3>
            <div class="space-y-2">
              <div>
                <label class="text-xs font-medium text-gray-600">Target Company Sizes</label>
                <p class="text-sm text-gray-900">{{ getArrayDisplay(icp.target_company_sizes) }}</p>
              </div>
              <div>
                <label class="text-xs font-medium text-gray-600">Target Company Revenue</label>
                <p class="text-sm text-gray-900">{{ getArrayDisplay(icp.target_company_revenues) }}</p>
              </div>
              <div>
                <label class="text-xs font-medium text-gray-600">Target Industries</label>
                <p class="text-sm text-gray-900">{{ getArrayDisplay(icp.target_industries) }}</p>
              </div>
              <div>
                <label class="text-xs font-medium text-gray-600">Target Countries</label>
                <p class="text-sm text-gray-900">{{ getArrayDisplay(icp.target_countries) }}</p>
              </div>
              <div>
                <label class="text-xs font-medium text-gray-600">Country Exclusion List</label>
                <p class="text-sm text-gray-900">{{ getArrayDisplay(icp.country_exclusions) }}</p>
              </div>
            </div>
          </div>

          <!-- Buyer Information -->
          <div class="bg-white rounded-lg border border-gray-200 p-4 xl:col-span-1">
            <h3 class="text-base font-semibold text-gray-900 mb-3 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 mr-1.5 text-violet-500">
                <path d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM14.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.615 16.428a1.224 1.224 0 0 1-.569-1.175 6.002 6.002 0 0 1 11.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 0 1 7 18a9.953 9.953 0 0 1-5.385-1.572ZM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 0 0-1.588-3.755 4.502 4.502 0 0 1 5.874 2.636.818.818 0 0 1-.36.98A7.465 7.465 0 0 1 14.5 16Z" />
              </svg>
              Target Buyers
            </h3>
            <div class="space-y-2">
              <div>
                <label class="text-xs font-medium text-gray-600">Target Departments</label>
                <p class="text-sm text-gray-900">{{ getDisplayValue(icp.main_buyer_department) }}</p>
              </div>
              <div>
                <label class="text-xs font-medium text-gray-600">Target Job Titles</label>
                <p class="text-sm text-gray-900">{{ getDisplayValue(icp.main_buyer_job_title) }}</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</div>
